export type LogoBackground = 'light' | 'dark';

export type LogoSize = 'favicon' | 'small' | 'medium' | 'large' | 'default';

export interface LogoVariant {
  size: LogoSize;
  src: string;
}

export interface LogoItem {
  id: string;
  name: string;
  background: LogoBackground;
  description?: string;
  variants: LogoVariant[];
}

export interface LogoGroup {
  title: string;
  items: LogoItem[];
}

/**
 * ATEK logo set — served directly from the public `atek-public-assets` bucket
 * so these URLs work anywhere (in-app, email signatures, external docs).
 * Full mirror of 03 Reference/ATEK Brand/logos/.
 */
export const PUBLIC_ASSETS_BASE = 'https://storage.googleapis.com/atek-public-assets/brand/logos';

function wordmarkVariants(prefix: string): LogoVariant[] {
  return [
    { size: 'small',   src: `${PUBLIC_ASSETS_BASE}/${prefix}-small.png` },
    { size: 'medium',  src: `${PUBLIC_ASSETS_BASE}/${prefix}-medium.png` },
    { size: 'large',   src: `${PUBLIC_ASSETS_BASE}/${prefix}-large.png` },
    { size: 'default', src: `${PUBLIC_ASSETS_BASE}/${prefix}.png` },
  ];
}

function iconVariants(prefix: string): LogoVariant[] {
  return [
    { size: 'favicon', src: `${PUBLIC_ASSETS_BASE}/${prefix}-favicon.png` },
    { size: 'small',   src: `${PUBLIC_ASSETS_BASE}/${prefix}-small.png` },
    { size: 'medium',  src: `${PUBLIC_ASSETS_BASE}/${prefix}-medium.png` },
    { size: 'large',   src: `${PUBLIC_ASSETS_BASE}/${prefix}-large.png` },
    { size: 'default', src: `${PUBLIC_ASSETS_BASE}/${prefix}.png` },
  ];
}

export const LOGO_GROUPS: LogoGroup[] = [
  {
    title: 'Primary wordmark',
    items: [
      {
        id: 'main-dark',
        name: 'Main — Dark',
        background: 'light',
        description: 'Default logo on light/bone backgrounds.',
        variants: wordmarkVariants('atek-logo-main-dark'),
      },
      {
        id: 'main-white',
        name: 'Main — White',
        background: 'dark',
        description: 'Default logo on dark/midnight/cobalt backgrounds.',
        variants: wordmarkVariants('atek-logo-main-white'),
      },
    ],
  },
  {
    title: 'Horizontal',
    items: [
      {
        id: 'horizontal-dark',
        name: 'Horizontal — Dark',
        background: 'light',
        description: 'Constrained vertical space: emails, headers.',
        variants: wordmarkVariants('atek-logo-horizontal-dark'),
      },
      {
        id: 'horizontal-white',
        name: 'Horizontal — White',
        background: 'dark',
        variants: wordmarkVariants('atek-logo-horizontal-white'),
      },
    ],
  },
  {
    title: 'Stacked',
    items: [
      {
        id: 'stacked-dark',
        name: 'Stacked — Dark',
        background: 'light',
        description: 'Square contexts: social avatars, app tiles.',
        variants: wordmarkVariants('atek-logo-stacked-dark'),
      },
      {
        id: 'stacked-white',
        name: 'Stacked — White',
        background: 'dark',
        variants: wordmarkVariants('atek-logo-stacked-white'),
      },
    ],
  },
  {
    title: 'Icon',
    items: [
      {
        id: 'icon-dark',
        name: 'Icon — Dark',
        background: 'light',
        description: 'Small sizes, favicons, UI chrome.',
        variants: iconVariants('atek-icon-dark'),
      },
      {
        id: 'icon-white',
        name: 'Icon — White',
        background: 'dark',
        variants: iconVariants('atek-icon-white'),
      },
    ],
  },
  {
    title: 'Tagline lockups',
    items: [
      {
        id: 'tagline-en',
        name: 'Tagline — EN (Monitoring Made Simple)',
        background: 'light',
        variants: wordmarkVariants('atek-logo-tagline-en-dark'),
      },
      {
        id: 'tagline-fr',
        name: 'Tagline — FR',
        background: 'light',
        variants: wordmarkVariants('atek-logo-tagline-fr-dark'),
      },
    ],
  },
  {
    title: 'Certifications',
    items: [
      {
        id: 'badge-a2la',
        name: 'A2LA Accredited',
        background: 'light',
        description: 'ISO/IEC 17025 accreditation badge.',
        variants: [
          { size: 'default', src: `${PUBLIC_ASSETS_BASE}/badge-a2la-accredited.png` },
          { size: 'large',   src: `${PUBLIC_ASSETS_BASE}/badge-a2la-accredited-large.png` },
        ],
      },
    ],
  },
];
